import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";

import { ApiError, api } from "../lib/api";
import { createCallAudio, type CallAudio } from "../lib/callAudio";
import {
  CallSession,
  type CallDeps,
  type CallSnapshot,
} from "../lib/callSession";
import { submitMessage } from "../lib/conversations";
import {
  getTtsVoice,
  setTtsVoice,
  uploadVoiceMessage,
  type TtsVoice,
} from "../lib/speech";
import * as store from "../lib/streamStore";
import { randomId } from "../lib/uuid";
import { TERMINAL_STATUSES, useStreams } from "../stream/StreamManager";
import { CallScreen } from "./CallScreen";

/**
 * Sesli gorusmenin SAHIBI - router'in USTUNDE (`StreamManager` gibi).
 *
 * ## Neden yazi kutusunun icinde DEGIL
 *
 * Yeni bir sohbette ilk mesaj gonderilince adres `/`den `/c/<id>`ye geciyor
 * ve yazi kutusu YENIDEN kuruluyor. Gorusme orada yasasaydi ilk turda
 * kapanirdi. Burada yasiyor; kendi konusma kimligini tutuyor ve kullanici
 * baska bir sohbete gecse bile gorusme kendi konusmasinda devam ediyor.
 *
 * ## Mesajlar normal sohbet yolundan
 *
 * Her tur `POST /api/chat` + sesli mesaj eki: gorusme bitince sohbet
 * listesinde normal bir konusma olarak duruyor, kullanicinin sozleri
 * oynatici + transkript olarak (kullanici karari). Run `StreamManager`a
 * devrediliyor; `ConversationView` akis listesi degisince konusmayi kendisi
 * yeniden yukluyor, yani ekran da canli guncelleniyor.
 */

interface CallContextValue {
  active: boolean;
  start(options: { conversationId: string | null; model: string | null }): void;
}

const CallContext = createContext<CallContextValue | null>(null);

export function CallProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { runs, attach, cancel } = useStreams();
  const [snapshot, setSnapshot] = useState<CallSnapshot | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [voice, setVoice] = useState<TtsVoice>(getTtsVoice);
  // Gorusme SURERKEN degisebilir: gonderim o anki secimi REF'ten okuyor.
  const modelRef = useRef<string | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const session = useRef<CallSession | null>(null);
  const audio = useRef<CallAudio | null>(null);
  const wakeLock = useRef<{ release(): Promise<void> } | null>(null);
  const conversation = useRef<string | null>(null);
  const runsRef = useRef(runs);
  const watchers = useRef(new Set<() => void>());

  // Takipci sekmede `store.finish` cagrilmiyor; bitis `runs` durumundan da
  // okunuyor ve degisince izleyiciler uyariliyor.
  runsRef.current = runs;
  const runSignature = runs.map((run) => `${run.id}:${run.status}`).join(",");
  useEffect(() => {
    for (const watcher of watchers.current) watcher();
  }, [runSignature]);

  const end = useCallback(() => {
    session.current?.end();
    session.current = null;
    audio.current?.close();
    audio.current = null;
    void wakeLock.current?.release().catch(() => undefined);
    wakeLock.current = null;
    setSnapshot(null);
  }, []);

  const start = useCallback<CallContextValue["start"]>(
    ({ conversationId, model: chosen }) => {
      if (session.current) return;
      conversation.current = conversationId;
      setModel(chosen);
      modelRef.current = chosen;
      setVoice(getTtsVoice());
      // TIKLAMA ANINDA: iOS ses kilidi (bkz. `createCallAudio`).
      const callAudio = createCallAudio();
      audio.current = callAudio;

      const deps: CallDeps = {
        openMic: () => callAudio.openMic(),
        player: callAudio.player,
        upload: uploadVoiceMessage,
        submit: async (text, attachmentId) => {
          const clientMessageId = randomId();
          for (let attempt = 0; ; attempt++) {
            try {
              const result = await submitMessage({
                conversationId: conversation.current,
                content: text,
                attachmentIds: [attachmentId],
                clientMessageId,
                model: modelRef.current,
                // Dusunme KAPALI: acik olsa her tura birkac saniye eklenirdi.
                think: "off",
                // Sunucu kisa, konusur gibi yanit veriyor; arac cagiracaksa
                // once "Hemen bakiyorum." diyor - bekleme sessiz gecmiyor.
                voice: true,
              });
              attach({
                id: result.run.id,
                conversation_id: result.conversation.id,
                assistant_message_id: result.assistant_message.id,
                status: "queued",
                model: null,
                think: result.assistant_message.think,
                chunk_count: 0,
                cancel_requested: false,
                error_code: null,
                queue_position: result.run.queue_position,
              });
              if (conversation.current === null) {
                conversation.current = result.conversation.id;
                navigate(`/c/${result.conversation.id}`);
              }
              return result.run.id;
            } catch (cause) {
              // Soz kesilince onceki yanit iptal edildi ama henuz bitmemis
              // olabilir: sunucu kisa bir sure "yanit uretiliyor" (409) der.
              if (
                cause instanceof ApiError &&
                cause.status === 409 &&
                attempt < 12
              ) {
                await new Promise((resolve) => setTimeout(resolve, 500));
                continue;
              }
              throw cause;
            }
          }
        },
        readRun: (runId) => {
          const buffer = store.getBuffer(runId);
          const run = runsRef.current.find((item) => item.id === runId);
          const status = run?.status ?? "running";
          const terminal = TERMINAL_STATUSES.has(status);
          return {
            text: buffer.full,
            done: buffer.netDone || terminal,
            failed: Boolean(buffer.error) || (terminal && status !== "done"),
          };
        },
        watchRun: (runId, onChange) => {
          watchers.current.add(onChange);
          const unsubscribe = store.subscribe(runId, onChange);
          return () => {
            watchers.current.delete(onChange);
            unsubscribe();
          };
        },
        cancelRun: (runId) => cancel(runId),
        tts: synthesize,
      };

      const call = new CallSession(deps, setSnapshot);
      session.current = call;
      setSnapshot(call.snapshot);
      setStartedAt(Date.now());
      // Telefonda ekran kararirsa tarayici mikrofonu kesiyor.
      const nav = navigator as Navigator & {
        wakeLock?: {
          request(type: "screen"): Promise<{ release(): Promise<void> }>;
        };
      };
      void nav.wakeLock
        ?.request("screen")
        .then((lock) => {
          wakeLock.current = lock;
        })
        .catch(() => undefined);
      void call.start();
    },
    [attach, cancel, navigate],
  );

  // Sayfadan cikarken mikrofonu birak.
  useEffect(() => () => end(), [end]);

  const value = useMemo<CallContextValue>(
    () => ({ active: snapshot !== null, start }),
    [snapshot, start],
  );

  return (
    <CallContext.Provider value={value}>
      {children}
      {snapshot && (
        <CallScreen
          snapshot={snapshot}
          model={model}
          startedAt={startedAt}
          onEnd={end}
          onToggleMute={() => session.current?.toggleMute()}
          onFinishTurn={() => session.current?.finishTurn()}
          voice={voice}
          onModelChange={(next) => {
            modelRef.current = next;
            setModel(next);
          }}
          onVoiceChange={(next) => {
            // Ayarlardaki okuma sesiyle ayni tercih: seslendirme her cumlede
            // `getTtsVoice()` okudugu icin bir sonraki cumleden gecerli.
            setTtsVoice(next);
            setVoice(next);
          }}
        />
      )}
    </CallContext.Provider>
  );
}

export function useCall(): CallContextValue | null {
  return useContext(CallContext);
}

async function synthesize(
  text: string,
  lang: string | null,
): Promise<{ audio: Blob; lang: string | null }> {
  const response = await api("/api/tts", {
    method: "POST",
    // Ses tercihi (varsayilan Chatterbox) - "sesli oku" ile ayni.
    body: JSON.stringify({
      text,
      voice: getTtsVoice(),
      ...(lang ? { lang } : {}),
    }),
  });
  if (!response.ok) throw new Error(String(response.status));
  return {
    audio: await response.blob(),
    lang: response.headers.get("X-TTS-Lang"),
  };
}
